/**
 * Hook UX adaptative plateforme (iOS ↔ Android Material).
 *
 * Orthogonal aux A/B tests d'interface (Home A/B, Employé V2 signature, etc.) :
 * le variant contrôle le contenu/layout, ce hook contrôle le *style d'interaction*.
 *
 * Stratégie : opt-in explicite par organisation via `platform_adaptive_ux`
 * (flag neutralisé par `PLATFORM_ADAPTIVE_UX_ENABLED: false` dans FEATURE_FLAGS
 * pour éviter le fallback "true" par défaut de isEnabled).
 *
 * - variant 'v1' : UX iOS-first actuelle (drawers Vaul iOS-feel, pull-to-refresh
 *   rubber-band, scrim blur, handle large, spring easing).
 * - variant 'v2' : UX adaptative. Sur iOS, le rendu reste identique à v1.
 *   Sur Android, les surfaces (drawers, pull-to-refresh, scrims) adoptent
 *   les tokens Material 3 (radius 28px, pas de scale background, handle discret,
 *   scrim bg-black/40 sans blur, easing standard/decelerate).
 *
 * Les composants consommateurs combinent `variant === 'v2'` avec
 * `getPlatform() === 'android'` via le flag dérivé `shouldAdaptAndroid`.
 *
 * ====================================================================
 * DEV OVERRIDE — visualiser Material UX sans passer par l'admin org
 * ====================================================================
 * Dans la console navigateur :
 *   // Forcer le mode Material Android (simuler un device Android + flag ON)
 *   localStorage.setItem('force-adaptive-ux', 'android-material');
 *   location.reload();
 *
 *   // Forcer le mode iOS par défaut (V1)
 *   localStorage.setItem('force-adaptive-ux', 'ios');
 *   location.reload();
 *
 *   // Désactiver l'override, revenir au comportement org/platform normal
 *   localStorage.removeItem('force-adaptive-ux');
 *   location.reload();
 *
 * L'override s'applique immédiatement à TOUS les composants adaptatifs
 * (Drawer, Sheet, Dialog, AlertDialog, Switch, Button ripple, Tabs,
 * DropdownMenu, Popover, Tooltip, SwipeableNotification, bottom nav, etc.).
 * Purement côté client — aucune persistance serveur, aucun impact sur
 * les autres utilisateurs de l'org.
 */
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { getPlatform } from '@/utils/platformDetection';

export type AdaptiveUXVariant = 'v1' | 'v2';

export interface AdaptiveUXResult {
  variant: AdaptiveUXVariant;
  /** True quand la variante adaptative doit s'appliquer effectivement (v2 + Android) */
  shouldAdaptAndroid: boolean;
  isLoading: boolean;
}

/**
 * Lit l'override dev côté client. Non-réactif (nécessite un reload après
 * modification) — c'est un outil de développement, pas une feature runtime.
 */
function readDevOverride(): 'android-material' | 'ios' | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage?.getItem('force-adaptive-ux');
    if (v === 'android-material' || v === 'ios') return v;
    return null;
  } catch {
    return null;
  }
}

export function useAdaptiveUX(): AdaptiveUXResult {
  const { isEnabled, isLoading } = useFeatureFlags();

  // Dev override prend la priorité absolue
  const override = readDevOverride();
  if (override === 'android-material') {
    return { variant: 'v2', shouldAdaptAndroid: true, isLoading: false };
  }
  if (override === 'ios') {
    return { variant: 'v1', shouldAdaptAndroid: false, isLoading: false };
  }

  const variant: AdaptiveUXVariant = isEnabled('platform_adaptive_ux') ? 'v2' : 'v1';
  const shouldAdaptAndroid = variant === 'v2' && getPlatform() === 'android';

  return { variant, shouldAdaptAndroid, isLoading };
}
